export const isPriceSort = (sort) => sort === 'price-ascending' || sort === 'price-descending';

const indexCache = new Map();
const cardCache = new Map();

export function orderProducts(products, sort) {
  return products.map((product, rank) => ({ ...product, rank })).sort((a, b) => {
    if (a.available !== b.available) return a.available ? -1 : 1;
    if (!isPriceSort(sort)) return a.rank - b.rank;
    const validA = Number.isFinite(a.price) && a.price > 0;
    const validB = Number.isFinite(b.price) && b.price > 0;
    if (validA !== validB) return validA ? -1 : 1;
    if (!validA) return a.rank - b.rank;
    const difference = sort === 'price-descending' ? b.price - a.price : a.price - b.price;
    return difference || a.rank - b.rank;
  });
}

export function selectPage(products, page, pageSize) {
  if (!Number.isInteger(page) || page < 1 || !Number.isInteger(pageSize) || pageSize < 1) {
    throw new Error('Invalid pagination');
  }
  return products.slice((page - 1) * pageSize, page * pageSize);
}

export async function mapLimited(items, limit, task, signal) {
  const output = new Array(items.length);
  let next = 0;
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      signal?.throwIfAborted();
      const index = next++;
      output[index] = await task(items[index], index);
    }
  }));
  return output;
}

export function sectionUrl(current, view, section, page, productUrl) {
  const context = new URL(current);
  const url = productUrl ? new URL(productUrl, context.origin) : new URL(context);
  if (url.origin !== context.origin) throw new Error('Unexpected product origin');
  if (productUrl && !url.pathname.includes('/products/')) throw new Error('Invalid product URL');
  if (productUrl) {
    url.search = '';
    for (const key of ['preview_theme_id', 'locale', 'currency']) {
      if (context.searchParams.has(key)) url.searchParams.set(key, context.searchParams.get(key));
    }
  }
  url.searchParams.delete('sections');
  url.searchParams.set('view', view);
  if (section) url.searchParams.set('section_id', section);
  else url.searchParams.delete('section_id');
  if (page) url.searchParams.set('page', String(page));
  else url.searchParams.delete('page');
  return url.pathname + url.search;
}

export function validateIndex(pages, expectedTotal) {
  const products = pages.flatMap((page) => page.products);
  if (pages.some((page, index) => page.version !== 1 || page.page !== index + 1 || page.total !== expectedTotal || page.pages !== pages.length)) {
    throw new Error('Collection changed during sorting');
  }
  const ids = new Set();
  for (const product of products) {
    if (!product || typeof product.id !== 'string' || !/^\d+$/.test(product.id) || ids.has(product.id) || typeof product.available !== 'boolean' || typeof product.url !== 'string' || typeof product.price !== 'number') {
      throw new Error('Invalid or duplicate sort record');
    }
    ids.add(product.id);
  }
  if (products.length !== expectedTotal) throw new Error('Incomplete collection index');
  return products;
}

export function mountSorter(doc, win) {
  let active;
  let generation = 0;

  function setLoading(loading, failed = false) {
    const container = doc.getElementById('ProductGridContainer');
    if (!container) return;
    const bootstrapPending = container.dataset.satsBootstrapPending === 'true';
    delete container.dataset.satsBootstrapPending;
    container.classList.toggle('sats-v2-loading', loading && bootstrapPending);
    container.classList.toggle('sats-v2-sorting', loading && !bootstrapPending);
    const grid = doc.getElementById('product-grid');
    if (grid) {
      grid.inert = loading;
      grid.setAttribute('aria-busy', String(loading));
      if (!loading && failed) grid.classList.remove('sats-v2-availability-pending');
    }
    const skeleton = container.querySelector('.sats-v2-skeleton');
    if (skeleton) skeleton.hidden = !loading || !bootstrapPending;
    const status = container.querySelector('[data-sats-sort-status]');
    if (status) {
      status.hidden = !loading && !failed;
      status.textContent = failed ? status.dataset.error : loading ? status.dataset.loading : '';
    }
  }

  function cancel() {
    generation++;
    active?.abort();
    active = undefined;
  }

  async function run() {
    cancel();
    const grid = doc.querySelector('#product-grid[data-sats-v2]');
    if (!grid) {
      setLoading(false);
      return;
    }
    const context = win.location.href;
    const signature = new URL(context).pathname + new URL(context).search;
    if (grid.dataset.satsSortedFor === signature) return;
    const token = generation;
    const controller = new AbortController();
    active = controller;
    const timeout = win.setTimeout(() => controller.abort(new Error('Collection sort timed out')), 15000);
    setLoading(true);
    let failed = false;
    try {
      const fetchDocument = async (url) => {
        const response = await win.fetch(url, { signal: controller.signal, credentials: 'same-origin', cache: 'default' });
        if (!response.ok) throw new Error('Sort response unavailable');
        return new win.DOMParser().parseFromString(await response.text(), 'text/html');
      };
      const indexContext = new URL(context);
      if (isPriceSort(grid.dataset.sort)) indexContext.searchParams.delete('sort_by');
      indexContext.searchParams.delete('page');
      const fetchIndex = async (page) => {
        const url = sectionUrl(indexContext.href, 'sats-v2-index', null, page);
        if (!indexCache.has(url)) {
          const request = fetchDocument(url)
            .then((html) => {
              const element = html.querySelector('[data-sats-index]');
              if (!element) throw new Error('Sort index missing');
              return JSON.parse(element.textContent);
            })
            .catch((error) => {
              indexCache.delete(url);
              throw error;
            });
          indexCache.set(url, request);
        }
        return indexCache.get(url);
      };
      const first = await fetchIndex(1);
      if (!Number.isInteger(first.pages) || first.pages < 1 || first.pages > 100) throw new Error('Invalid index pagination');
      const remaining = await mapLimited(Array.from({ length: first.pages - 1 }, (_, index) => index + 2), 3, fetchIndex, controller.signal);
      const products = validateIndex([first, ...remaining], Number(grid.dataset.total));
      const sorted = orderProducts(products, grid.dataset.sort);
      const selected = selectPage(sorted, Number(grid.dataset.page), Number(grid.dataset.pageSize));
      if (!selected.length && products.length) throw new Error('Requested page unavailable');
      const cards = await mapLimited(selected, 12, async (product) => {
        const url = sectionUrl(context, 'sats-v2-card', null, null, product.url);
        if (!cardCache.has(url)) {
          const request = fetchDocument(url)
            .then((html) => {
              const card = html.querySelector('[data-sats-m2-card]');
              if (!card || card.dataset.satsProductId !== product.id || (card.dataset.satsAvailable === 'true') !== product.available || Math.round(Number(card.dataset.satsM2Price)) !== product.price) {
                throw new Error('Product changed during sorting');
              }
              card.querySelectorAll('script').forEach((script) => script.remove());
              if (card.dataset.satsAppendCm === 'true') {
                card.querySelectorAll('.card__heading .full-unstyled-link').forEach((link) => {
                  if (!/\s+cm$/i.test(link.textContent.trim())) link.append(doc.createTextNode(' cm'));
                });
              }
              return card;
            })
            .catch((error) => {
              cardCache.delete(url);
              throw error;
            });
          cardCache.set(url, request);
        }
        return doc.importNode(await cardCache.get(url), true);
      }, controller.signal);
      controller.signal.throwIfAborted();
      if (token !== generation || !grid.isConnected || win.location.href !== context) return;
      grid.replaceChildren(...cards);
      grid.classList.remove('sats-v2-availability-pending');
      grid.dataset.satsSortedFor = signature;
    } catch (error) {
      failed = token === generation;
      controller.abort();
      if (failed) grid.dataset.satsSortFailed = 'true';
    } finally {
      win.clearTimeout(timeout);
      if (token === generation) setLoading(false, failed);
    }
  }

  doc.addEventListener('sats:facets-start', (event) => {
    cancel();
    setLoading(Boolean(event.detail?.sort));
  });
  doc.addEventListener('sats:facets-ready', run);
  doc.addEventListener('sats:facets-error', () => { cancel(); setLoading(false, true); });
  win.addEventListener('pagehide', cancel);
  win.addEventListener('pageshow', (event) => { if (event.persisted) run(); });
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', run, { once: true });
  else run();
  return { run, cancel };
}

if (typeof document !== 'undefined') mountSorter(document, window);
